import { Component, computed, OnInit, signal } from '@angular/core';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DividerModule } from 'primeng/divider';
import { SelectButtonModule } from 'primeng/selectbutton';
import { MessagesModule } from 'primeng/messages';
import { DropdownModule } from 'primeng/dropdown';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

import { WaitDaysOFWeekNode } from '@modules/campaigns/Domain/Nodes/WaitDaysOfWeekNode';
import { WaitDaysOfWeekConfig } from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { InspectorBase } from '@shared/components/joint-js/components/inspectors/inspector-base';
import { RegionalConfigInfoComponent } from '@shared/components/regional-config-info/regional-config-info.component';
import { CampaignModel } from '@modules/campaigns/models/campaign.model';
import { CampaignContextService } from '@shared/services/campaign-context.service';

@Component({
  selector: 'app-wait-day-of-week-inspector',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    FormsModule,
    TranslateModule,
    DividerModule,
    SelectButtonModule,
    MessagesModule,
    DropdownModule,
    CardModule,
    ButtonModule,
    TooltipModule,
    RegionalConfigInfoComponent,
  ],
  templateUrl: './wait-day-of-week-inspector.component.html',
  styleUrls: ['./wait-day-of-week-inspector.component.scss'],
})
export class WaitDayOfWeekInspectorComponent
  extends InspectorBase<WaitDaysOFWeekNode>
  implements OnInit
{
  partDay = signal<string | null>('MORNING');
  dayOfWeek = signal<string | null>('MONDAY');
  campaignSelected = signal<CampaignModel | null>(null);

  partDayOptions = computed(() => [
    {
      label: this.translate.instant('DATA.COMMON.TIME_OF_WEEK.MORNING'),
      value: 'MORNING',
    },
    {
      label: this.translate.instant('DATA.COMMON.TIME_OF_WEEK.EVENING'),
      value: 'EVENING',
    },
    {
      label: this.translate.instant('DATA.COMMON.TIME_OF_WEEK.FULL_DAY'),
      value: 'FULL_DAY',
    },
  ]);

  dayOfWeekOptions = computed(() => [
    {
      label: this.translate.instant('DATA.COMMON.DAYS_OF_WEEK.MONDAY'),
      value: 'MONDAY',
    },
    {
      label: this.translate.instant('DATA.COMMON.DAYS_OF_WEEK.TUESDAY'),
      value: 'TUESDAY',
    },
    {
      label: this.translate.instant('DATA.COMMON.DAYS_OF_WEEK.WEDNESDAY'),
      value: 'WEDNESDAY',
    },
    {
      label: this.translate.instant('DATA.COMMON.DAYS_OF_WEEK.THURSDAY'),
      value: 'THURSDAY',
    },
    {
      label: this.translate.instant('DATA.COMMON.DAYS_OF_WEEK.FRIDAY'),
      value: 'FRIDAY',
    },
    {
      label: this.translate.instant('DATA.COMMON.DAYS_OF_WEEK.SATURDAY'),
      value: 'SATURDAY',
    },
    {
      label: this.translate.instant('DATA.COMMON.DAYS_OF_WEEK.SUNDAY'),
      value: 'SUNDAY',
    },
  ]);

  constructor(
    private workflow: WorkflowStateService,
    private translate: TranslateService,
    private campaignContext: CampaignContextService,
  ) {
    super();
  }

  ngOnInit() {
    this.loadCampaignContext();
  }

  override get nodeValue(): WaitDaysOFWeekNode | null {
    const current = this.node();
    if (!current) return null;
    return current.type === NodeType.WaitDayOfWeek ? (current as WaitDaysOFWeekNode) : null;
  }

  getTabs() {
    return [
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.WAIT_DAY_OF_WEEK.TABS.PREVIEW'),
        value: 'preview' as const,
      },
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.WAIT_DAY_OF_WEEK.TABS.CONFIG'),
        value: 'config' as const,
      },
    ];
  }

  onPartDayChange(value: string) {
    this.partDay.set(value);
    this.scheduleApplyChanges();
  }

  onDayOfWeekChange(value: string) {
    this.dayOfWeek.set(value);
    this.scheduleApplyChanges();
  }

  getSummaryMessage(): string {
    const partDay = this.getPartDayLabel().toLowerCase();
    const dayOfWeek = this.getDayOfWeekLabel();

    return this.translate.instant(
      'DATA.MODULES.INSPECTORS.WAIT_DAY_OF_WEEK.PREVIEW.SUMMARY_MESSAGE',
      {
        partDay,
        dayOfWeek,
      },
    );
  }

  private loadCampaignContext() {
    this.campaignContext.campaign$.subscribe((campaign) => {
      if (campaign) {
        this.campaignSelected.set(campaign);
      }
    });
  }

  private getPartDayLabel(): string {
    const partDay = this.partDay();
    if (partDay === 'MORNING') {
      return this.translate.instant('DATA.COMMON.TIME_OF_WEEK.MORNING');
    } else if (partDay === 'EVENING') {
      return this.translate.instant('DATA.COMMON.TIME_OF_WEEK.EVENING');
    } else if (partDay === 'FULL_DAY') {
      return this.translate.instant('DATA.COMMON.TIME_OF_WEEK.FULL_DAY');
    }
    return '-';
  }

  private getDayOfWeekLabel(): string {
    const dayOfWeek = this.dayOfWeek();
    const daysMap: { [key: string]: string } = {
      MONDAY: 'DATA.COMMON.DAYS_OF_WEEK.MONDAY',
      TUESDAY: 'DATA.COMMON.DAYS_OF_WEEK.TUESDAY',
      WEDNESDAY: 'DATA.COMMON.DAYS_OF_WEEK.WEDNESDAY',
      THURSDAY: 'DATA.COMMON.DAYS_OF_WEEK.THURSDAY',
      FRIDAY: 'DATA.COMMON.DAYS_OF_WEEK.FRIDAY',
      SATURDAY: 'DATA.COMMON.DAYS_OF_WEEK.SATURDAY',
      SUNDAY: 'DATA.COMMON.DAYS_OF_WEEK.SUNDAY',
    };
    return dayOfWeek && daysMap[dayOfWeek] ? this.translate.instant(daysMap[dayOfWeek]) : '-';
  }

  override applyChanges() {
    if (this.mode() === 'preview') return;

    const node = this.nodeValue;
    if (!node) return;

    const config: WaitDaysOfWeekConfig = {
      partDay: this.partDay() || null,
      dayOfWeek: this.dayOfWeek() || null,
    };

    node.config = config;
    const validation = node.validate();
    this.errors.set(validation);

    if (!validation.length) {
      this.workflow.addOrUpdateNode(node);
    }
  }

  override refreshFromConfig() {
    const node = this.nodeValue;
    if (!node) return;

    const cfg = (node.config ?? {}) as WaitDaysOfWeekConfig;

    this.partDay.set(cfg.partDay ?? 'MORNING');
    this.dayOfWeek.set(cfg.dayOfWeek ?? 'MONDAY');

    this.errors.set([]);
  }

  protected override scheduleApplyChanges() {
    clearTimeout(this.changeTimeout);
    this.changeTimeout = setTimeout(() => this.applyChanges(), 500);
  }
}
